import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRxCollection } from 'rxdb/plugins/react';
import type {
    AreaDescriptionDocType,
    MediaDocType,
    MediaLinkDocType,
    MediaGroupDocType,
    ParcelDocType,
    ProjectDocType,
    TranslationDocType
} from '@ast-data/shared';
import { useToast } from '../../../components/toastContext';
import { downloadBlob } from '../../../utils/downloadFile';
import { confirmUnavailableMedia } from '../pdf/confirmUnavailableMedia';
import { useConfirm } from '../../../components/confirmContext';
import { createDeliverableTexts } from '../../translation/deliverableTexts';
import { confirmTranslationGaps } from '../../translation/confirmTranslationGaps';
import {
    collectAreaDescriptionExportData,
    areaDescriptionPdfFileName,
    type AreaDescriptionExportCollections
} from './exportData';
import { usePersonLabels } from '../../users/personLabels';
import { recordMeasureInfo } from '../../projects/projectTitle';

/**
 * Export Flächenbeschreibungen to a single PDF and download it.
 *
 * `exportPdf(ids)` renders the given records, whatever Maßnahme each is from —
 * every sheet names its own — so the project grid and the Auswertung use this
 * the same way. `busy` is true while rendering.
 */
export function useAreaDescriptionPdfExport() {
    const intl = useIntl();
    const { notify } = useToast();
    const { confirm } = useConfirm();
    const [busy, setBusy] = useState(false);

    // The sheets name people by their accounts, as the screens do.
    const personLabels = usePersonLabels();
    const projects = useRxCollection<ProjectDocType>('projects');
    const areaDescriptions =
        useRxCollection<AreaDescriptionDocType>('area_descriptions');
    const parcels = useRxCollection<ParcelDocType>('parcels');
    const media = useRxCollection<MediaDocType>('media');
    const mediaLinks = useRxCollection<MediaLinkDocType>('media_links');
    const mediaGroups = useRxCollection<MediaGroupDocType>('media_groups');
    const translations = useRxCollection<TranslationDocType>('translations');

    const exportPdf = useCallback(
        async (ids: readonly string[]) => {
            if (
                !projects ||
                !areaDescriptions ||
                !parcels ||
                !media ||
                !mediaLinks ||
                !mediaGroups ||
                ids.length === 0
            ) {
                return;
            }
            setBusy(true);
            try {
                const collections: AreaDescriptionExportCollections = {
                    projects,
                    areaDescriptions,
                    parcels,
                    media,
                    mediaLinks,
                    mediaGroups
                };
                const deliverable = createDeliverableTexts(
                    translations,
                    projects
                );
                const data = await collectAreaDescriptionExportData({
                    collections,
                    ids,
                    labelOf: personLabels.labelOf,
                    deliverable
                });
                // Nothing printable: the records are gone, or their Maßnahme
                // is not on this device.
                if (data.areaDescriptions.length === 0) {
                    notify(
                        intl.formatMessage({ id: 'areaDescriptionPdf.empty' }),
                        'info'
                    );
                    return;
                }
                // Offline, pictures not yet on this device cannot print — ask
                // before rendering a PDF without them.
                if (
                    !(await confirmUnavailableMedia(
                        confirm,
                        intl,
                        data.areaDescriptions.flatMap((entry) => entry.media)
                    ))
                ) {
                    return;
                }
                if (
                    !(await confirmTranslationGaps(
                        confirm,
                        intl,
                        deliverable.gaps
                    ))
                ) {
                    return;
                }
                // The renderer arrives here, at the click — see renderPdf.
                const { renderAreaDescriptionPdf } =
                    await import('./renderPdf');
                const blob = await renderAreaDescriptionPdf(data, intl);
                downloadBlob(
                    blob,
                    areaDescriptionPdfFileName(
                        data.areaDescriptions.map(
                            (entry) =>
                                recordMeasureInfo(entry.project, {
                                    recordedAt: entry.doc.recordedAt
                                }).measureNumber
                        ),
                        data.areaDescriptions.map((entry) => entry.doc)
                    )
                );
            } catch (cause) {
                notify(
                    intl.formatMessage({ id: 'areaDescriptionPdf.error' }),
                    'error'
                );
                console.error('Flächenbeschreibung PDF export failed', cause);
            } finally {
                setBusy(false);
            }
        },
        [
            personLabels,
            projects,
            areaDescriptions,
            parcels,
            media,
            mediaLinks,
            mediaGroups,
            translations,
            intl,
            notify,
            confirm
        ]
    );

    return { exportPdf, busy };
}
